import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Table, Input, Row, Col, Tag, message, Button } from 'antd';
import { debounce } from 'lodash';
import { DoubleLine } from "../../Components/Labels";
import { portfolioTypeString } from "../../utils/strings";

const { Search } = Input;

const PortfolioSelector = ({ onSelect, initialSelectedPortfolios = [], allowTypes = ['b', 's', 'm'], maxSelected = 20 }) => {
  const portfolios = useSelector(state => state.portfolios.items);
  const [selectedRowKeys, setSelectedRowKeys] = useState(initialSelectedPortfolios.map(p => p.id));
  const [selectedPortfolios, setSelectedPortfolios] = useState(initialSelectedPortfolios);
  const [searchValue, setSearchValue] = useState("");

  const filteredPortfolios = useMemo(() => {
    return portfolios.filter(portfolio =>
      allowTypes.includes(portfolio.type) &&
      portfolio?.name?.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [portfolios, allowTypes, searchValue]);

  const onSelectChange = useCallback((newRowKeys, newRows) => {
    if (newRowKeys.length > maxSelected) {
      message.warning(`You can only select up to ${maxSelected} portfolios.`);
      return;
    }
    setSelectedRowKeys([...selectedRowKeys, ...newRowKeys]);
    setSelectedPortfolios([...newRows, ...selectedPortfolios]);
  }, [maxSelected]);

  const handleSearch = useCallback(debounce((value) => {
    setSearchValue(value);
  }, 300), []);

  const handleSubmit = useCallback(() => {
    onSelect(selectedPortfolios);
  }, [onSelect, selectedPortfolios]);

  const handleTagClose = useCallback((portfolioId) => {
    const newSelectedPortfolios = selectedPortfolios.filter(p => p.id !== portfolioId);
    setSelectedPortfolios(newSelectedPortfolios);
    setSelectedRowKeys(newSelectedPortfolios.map(p => p.id));
    onSelect(newSelectedPortfolios);
  }, [selectedPortfolios, onSelect]);

  const columns = [
    {
      title: 'Portfolio',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <DoubleLine
          title={record.name}
          text={portfolioTypeString(record.type)}
        />
      )
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <div style={{ maxWidth: 800, margin: 'auto' }}>
      <Search
        placeholder="Search portfolios"
        onChange={(e) => handleSearch(e.target.value)}
        style={{ marginBottom: 16 }}
        enterButton={<Button type={'primary'} disabled={selectedPortfolios.length === 0} onClick={handleSubmit}>SELECT PORTFOLIOS</Button>}
      />
      {selectedPortfolios.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            {selectedPortfolios.map(portfolio => (
              <Col key={portfolio.id}>
                <Tag style={{ fontSize: '16px', padding: '10px 6px' }} bordered={false} color={'blue'} closable onClose={() => handleTagClose(portfolio.id)}>
                  {portfolio.name}
                </Tag>
              </Col>
            ))}
          </Row>
        </div>
      )}
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={filteredPortfolios}
        rowKey="id"
        pagination={false}
        scroll={{ y: 480 }}
      />
    </div>
  );
};

export default PortfolioSelector;
